import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Settings } from 'lucide-react';

import { DraftSources } from '@data';
import { useAiSummaryInstructions, useSetAiSummaryInstructions, useSetDraftSources } from '@data/hooks/membership';
import { Button, buttonVariants } from '@ui/components/ui/button';
import { Popover, PopoverContent, PopoverTitle, PopoverTrigger } from '@ui/components/ui/popover';
import { Textarea } from '@ui/components/ui/textarea';
import { cn } from '@ui/lib/utils';
import { GithubIcon, GoogleCalendarIcon, SlackIcon } from '@app/components/core/philBot/appIcons';
import { useConnectSource } from '@app/components/core/philBot/useConnectSource';

import { PillContent, pillClass } from './pill';

import type { ReadingSource } from './readingSources';

const MAX_INSTRUCTIONS = 500;

const ALL_SOURCES = [
  { setting: 'draftReadsGithub', app: 'github', label: 'phillReport.sourceGithub', Icon: GithubIcon },
  { setting: 'draftReadsSlack', app: 'slack', label: 'phillReport.sourceSlack', Icon: SlackIcon },
  {
    setting: 'draftReadsCalendar',
    app: 'googleCalendar',
    label: 'phillReport.sourceCalendar',
    Icon: GoogleCalendarIcon,
  },
] as const;

export interface DraftSettingsProps {
  membershipId?: string;
  sources: ReadingSource[];
  className?: string;
}

/** The gear beside the close button: what drafting reads, and the person's own instructions for it. */
const DraftSettings = ({ membershipId, sources, className }: DraftSettingsProps): JSX.Element => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [instructions, setInstructions] = useState('');
  const { data: stored } = useAiSummaryInstructions(membershipId);
  const { mutate: save, mutateAsync: saveBeforeLeaving } = useSetAiSummaryInstructions(membershipId);
  const { mutate: saveSources } = useSetDraftSources(membershipId);
  // What was just switched, shown until the saved answer replaces it.
  const [switched, setSwitched] = useState<Partial<DraftSources>>({});
  const saved = stored?.aiSummaryInstructions ?? '';

  const isOn = (setting: keyof DraftSources): boolean => switched[setting] ?? stored?.[setting] !== false;
  const toggle = (setting: keyof DraftSources, next: boolean): void => {
    setSwitched(current => ({ ...current, [setting]: next }));
    const forget = (): void =>
      setSwitched(current => {
        const rest = { ...current };
        delete rest[setting];
        return rest;
      });
    saveSources({ [setting]: next }, { onSettled: forget });
  };

  const changeOpen = (next: boolean): void => {
    if (next) setInstructions(saved);
    else if (instructions.trim() !== saved) save(instructions.trim());
    setOpen(next);
  };

  // A blocked popup leaves the page, which would cancel a save still in flight, or lose one that failed.
  const connect = useConnectSource(async () => {
    if (instructions.trim() === saved) return true;
    try {
      await saveBeforeLeaving(instructions.trim());
      return true;
    } catch {
      // The global error toast already says why; staying keeps what was typed.
      return false;
    }
  });

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger
        aria-label={t('phillReport.draftSettings')}
        className={buttonVariants({ variant: 'ghost', size: 'icon-sm', className })}
      >
        <Settings />
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[22rem] gap-3 p-3">
        <PopoverTitle>{t('phillReport.draftWithAi')}</PopoverTitle>
        <div className="flex flex-col gap-1.5">
          <p className="text-xs font-medium text-foreground">{t('phillReport.readsFrom')}</p>
          <div className="flex gap-1.5">
            {ALL_SOURCES.map(({ setting, app, label, Icon }) => {
              const source = sources.find(candidate => candidate.setting === setting);
              const pressed = !!source && isOn(setting);

              return (
                <button
                  key={setting}
                  type="button"
                  aria-pressed={pressed}
                  className={cn(pillClass(pressed), 'disabled:cursor-not-allowed disabled:opacity-45')}
                  disabled={!!source && !membershipId}
                  title={source ? source.detail : t('phillReport.connectSource', { source: t(label) })}
                  onClick={() => {
                    if (source) {
                      toggle(setting, !pressed);
                      return;
                    }
                    connect(app);
                  }}
                >
                  <PillContent pressed={pressed}>
                    <Icon className="size-3.5 shrink-0" />
                    {t(label)}
                  </PillContent>
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="phill-draft-instructions" className="text-xs font-medium text-foreground">
            {t('phillReport.yourInstructions')}
          </label>
          <Textarea
            id="phill-draft-instructions"
            rows={3}
            value={instructions}
            onChange={event => setInstructions(event.target.value)}
            maxLength={MAX_INSTRUCTIONS}
            disabled={!membershipId}
            placeholder={t('philBot.instructionsPlaceholder')}
            className="text-xs"
          />
        </div>
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={() => changeOpen(false)}>
            {t('phillReport.done')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DraftSettings;
